import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const KEY = "omega_cookie_consent";

function read() {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (_) {
        return null;
    }
}

function write(status) {
    if (typeof window === "undefined") return;
    const payload = { status, timestamp: new Date().toISOString() };
    window.localStorage.setItem(KEY, JSON.stringify(payload));
    return payload;
}

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
    const [consent, setConsent] = useState(() => read());
    const [bannerOpen, setBannerOpen] = useState(false);

    useEffect(() => {
        // Show banner on first visit or when user has explicitly re-opened
        if (!read()) setBannerOpen(true);
    }, []);

    const accept = useCallback(() => {
        const p = write("all");
        setConsent(p);
        setBannerOpen(false);
    }, []);
    const essentialOnly = useCallback(() => {
        const p = write("essential_only");
        setConsent(p);
        setBannerOpen(false);
    }, []);
    const openSettings = useCallback(() => setBannerOpen(true), []);
    const openBanner = openSettings; // alias
    const closeBanner = useCallback(() => setBannerOpen(false), []);

    const value = useMemo(() => ({
        consent,
        status: consent?.status || null,
        allowsTracking: consent?.status === "all",
        bannerOpen,
        accept,
        essentialOnly,
        openSettings,
        openBanner,
        closeBanner,
    }), [consent, bannerOpen, accept, essentialOnly, openSettings, openBanner, closeBanner]);

    return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
    const ctx = useContext(CookieConsentContext);
    if (!ctx) throw new Error("useCookieConsent must be used inside CookieConsentProvider");
    return ctx;
}
